"use client";

import Link from "next/link";
import type {
  CBlogArticle,
  CBlogContent,
  CBlogItem,
} from "@/lib/api/cms-catalog";
import dynamic from "next/dynamic";
import { useT } from "@/lib/i18n/context";
import { cBlogTranslations, type CBlogTranslations } from "./CBlog.i18n";
import { ParagraphRenderer } from "@/components/cms/Paragraphs/ParagraphRenderer";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CBlog.module.css";

/* Biblioteka CMS Interakcje (komentarze + reakcje) - ładowana leniwie
   (ssr:false), montuje się i dociąga dane dopiero po wyrenderowaniu wpisu. */
const ArticleInteractions = dynamic(
  () =>
    import("@/components/cms/Interactions/ArticleInteractions").then(
      (m) => m.ArticleInteractions,
    ),
  { ssr: false },
);

/**
 * CBlog - visitor view komponentu `c_blog` (Blog).
 *
 * **Data source:** `C_BLOG_CONTROLLER::getApiContent()` zwraca:
 *   - `list` - paginowana lista wpisow (item: id, name, short_text,
 *     publication_date, count_view, picture, href)
 *   - `article` - pojedynczy wpis (meta + autor + kategorie) + akapity
 *     (`imp_paragraph`) renderowane przez `<ParagraphRenderer>`.
 *
 * Tresc wpisu = wylacznie akapity (c_blog nie renderuje juz akapitow server-side -
 * `getHtmlParagraphs` usuniety). Media (zdjecia/pliki) siedza w akapitach.
 *
 * **Security:** `short_text` to HTML z admin panelu (trusted) - OK dla
 * `dangerouslySetInnerHTML`.
 */
export function CBlog({ content, title, subtitle }: CBlogProps) {
  const t = useT(cBlogTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "article" && content.article ? (
        <ArticleView
          article={content.article}
          listHref={content.list_href ?? null}
          t={t}
        />
      ) : (
        <ListView content={content} title={title} subtitle={subtitle} t={t} />
      )}
    </div>
  );
}

export type CBlogProps = {
  content: CBlogContent;
  /** Nazwa katalogu (`imp_cms_catalog.name`) - eyebrow nad tytulem sekcji listy. */
  title?: string | null;
  /** Podtytul katalogu (`imp_cms_catalog.name_subtitle`) - big H1 tytul + opcjonalny opis. */
  subtitle?: string | null;
};

/* List view */

function ListView({
  content,
  title,
  subtitle,
  t,
}: {
  content: CBlogContent;
  title?: string | null;
  subtitle?: string | null;
  t: CBlogTranslations;
}) {
  const items: CBlogItem[] = Array.isArray(content.items) ? content.items : [];
  const pagination = content.pagination;

  //Priorytet: config katalogu (hero_text/hero_title/hero_description) > fallback do
  //nazwy katalogu (title/subtitle z Container). Admin moze wpisac wlasny eyebrow +
  //big title + description dla listy artykulow.
  const eyebrow =
    content.hero_text && content.hero_text.trim() !== "" ? content.hero_text : title;
  const heading =
    content.hero_title && content.hero_title.trim() !== ""
      ? content.hero_title
      : subtitle && subtitle.trim() !== ""
        ? subtitle
        : title;
  const description =
    content.hero_description && content.hero_description.trim() !== ""
      ? content.hero_description
      : "";

  const hasEyebrow = !!eyebrow && eyebrow.trim() !== "" && eyebrow !== heading;
  const hasHeading = !!heading && heading.trim() !== "";
  const hasDescription = description !== "";

  return (
    <>
      {(hasEyebrow || hasHeading || hasDescription) && (
        <div className={styles.listHeaderBg}>
          <div className={styles.listHeaderInner}>
            {hasEyebrow ? <p className={styles.listEyebrow}>{eyebrow}</p> : null}
            {hasHeading ? <h1 className={styles.listHeading}>{heading}</h1> : null}
            {hasDescription ? (
              <p className={styles.listDescription}>{description}</p>
            ) : null}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className={styles.empty}>{t.emptyList}</div>
      ) : (
        <CatalogLoadMore<CBlogItem>
          initialItems={items}
          initialPage={pagination?.page ?? 1}
          totalPages={pagination?.total_pages ?? 1}
          totalCount={pagination?.total_count}
          getKey={(item) => item.id}
          extractItems={(c) => {
            const arr = (c as CBlogContent | null)?.items;
            return Array.isArray(arr) ? arr : [];
          }}
        >
          {(allItems) => (
            <ul className={styles.list}>
              {allItems.map((item) => (
                <BlogCard key={item.id} item={item} t={t} />
              ))}
            </ul>
          )}
        </CatalogLoadMore>
      )}
    </>
  );
}

function BlogCard({ item, t }: { item: CBlogItem; t: CBlogTranslations }) {
  const href = extractInternalHref(item.href);
  //Reading time: liczymy tylko ze short_text (backend nie zwraca akapitow w list view).
  //Dziala jako proxy - lepsze niz nic. Full estimate w article view przez estimateReadingMinutes.
  const readingMinutes = estimateReadingMinutesFromHtml(item.short_text ?? "");

  const cardInner = (
    <>
      <div className={styles.thumbWrap}>
        {item.picture ? (
          <img
            src={item.picture.thumb_src}
            alt={item.picture.alt || item.name}
            width={item.picture.width ?? undefined}
            height={item.picture.height ?? undefined}
            className={styles.thumb}
            loading="lazy"
          />
        ) : (
          <div className={styles.thumbPlaceholder} aria-hidden="true" />
        )}
      </div>
      <div className={styles.body}>
        {(item.publication_date || readingMinutes > 0) && (
          <p className={styles.cardMeta}>
            {item.publication_date && (
              <time dateTime={item.publication_date}>
                {t.formatDate(item.publication_date)}
              </time>
            )}
            {item.publication_date && readingMinutes > 0 && (
              <span aria-hidden="true"> · </span>
            )}
            {readingMinutes > 0 && <span>{t.readingMinutes(readingMinutes)}</span>}
          </p>
        )}
        <h2 className={styles.title}>{item.name}</h2>
        <div
          className={styles.short}
          dangerouslySetInnerHTML={{ __html: item.short_text }}
        />
        <span className={styles.more}>{t.readMore}</span>
      </div>
    </>
  );

  return (
    <li className={styles.card}>
      {href !== null ? (
        <Link href={href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </Link>
      ) : (
        <a href={item.href} className={styles.cardLink} aria-label={item.name}>
          {cardInner}
        </a>
      )}
    </li>
  );
}

/**
 * Szacunkowy czas czytania z raw HTML - strip tagow, count slow, 200 wpm.
 * Minimum 1 minuta gdy jest jakikolwiek content. 0 gdy pusto.
 */
function estimateReadingMinutesFromHtml(html: string): number {
  const stripped = html.replace(/<[^>]+>/g, " ");
  const words = stripped.split(/\s+/).filter((w) => w.length > 0).length;
  if (words === 0) return 0;
  return Math.max(1, Math.round(words / 200));
}

/* Article view */

function ArticleView({
  article,
  listHref,
  t,
}: {
  article: CBlogArticle;
  listHref: string | null;
  t: CBlogTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;
  const firstCategory = article.categorys?.[0] ?? null;
  const readingMinutes = estimateReadingMinutes(article);
  const authorInitial = article.author?.name
    ? article.author.name.trim().charAt(0).toUpperCase()
    : "";

  return (
    <article className={styles.article}>
      <div className={styles.articleHeaderBg}>
        <div className={styles.articleHeaderInner}>
          <div className={styles.articleTopMeta}>
            {(backHref !== null || listHref) && (
              backHref !== null ? (
                <Link href={backHref} className={styles.articleBackLink}>
                  <span aria-hidden="true">←</span> {t.backToList}
                </Link>
              ) : (
                <a href={listHref!} className={styles.articleBackLink}>
                  <span aria-hidden="true">←</span> {t.backToList}
                </a>
              )
            )}
            {firstCategory && (
              <span className={styles.articleCategoryPill}>
                {firstCategory.name}
              </span>
            )}
            {(article.publication_date || readingMinutes > 0) && (
              <span className={styles.articleTopDate}>
                {article.publication_date && (
                  <time dateTime={article.publication_date}>
                    {t.formatDate(article.publication_date)}
                  </time>
                )}
                {article.publication_date && readingMinutes > 0 && (
                  <span aria-hidden="true"> · </span>
                )}
                {readingMinutes > 0 && (
                  <span>{t.readingMinutes(readingMinutes)}</span>
                )}
              </span>
            )}
          </div>

          <header className={styles.articleHeader}>
            <h1 className={styles.articleTitle}>{article.name}</h1>
            {article.author?.name && (
              <div className={styles.articleAuthorRow}>
                <span className={styles.articleAuthorAvatar} aria-hidden="true">
                  {authorInitial}
                </span>
                <span className={styles.articleAuthorName}>{article.author.name}</span>
              </div>
            )}
          </header>
        </div>
      </div>

      {article.picture && (
        <figure className={styles.articleMainImage}>
          <img
            src={article.picture.full_src}
            alt={article.picture.alt || article.name}
            width={article.picture.width ?? undefined}
            height={article.picture.height ?? undefined}
            loading="eager"
          />
        </figure>
      )}

      {article.short_text && (
        <div
          className={styles.articleLead}
          dangerouslySetInnerHTML={{ __html: article.short_text }}
        />
      )}

      {article.paragraphs && article.paragraphs.length > 0 && (
        <ParagraphRenderer paragraphs={article.paragraphs} />
      )}

      {/* Reakcje + komentarze (biblioteka CMS Interakcje) - lazy, pod tresca
          wpisu. Reakcje renderowane nad komentarzami. */}
      {article.interactions &&
        (article.interactions.reactions?.enabled ||
          article.interactions.comments?.enabled) && (
          <ArticleInteractions interactions={article.interactions} />
        )}
    </article>
  );
}

/**
 * Szacunkowy czas czytania w minutach - liczy slowa z short_text + tekstowych
 * pol wszystkich akapitow (`name`, `text`, `text_1`, `text_2`), 200 wpm.
 * Minimum 1 minuta gdy jest jakikolwiek content. 0 gdy pusto.
 */
function estimateReadingMinutes(article: CBlogArticle): number {
  const strip = (html: string) => html.replace(/<[^>]+>/g, " ");
  const countWords = (s: string) =>
    s.split(/\s+/).filter((w) => w.length > 0).length;

  let words = countWords(strip(article.short_text ?? ""));
  for (const p of article.paragraphs ?? []) {
    const pp = p as Record<string, unknown>;
    for (const key of ["name", "text", "text_1", "text_2"]) {
      const v = pp[key];
      if (typeof v === "string" && v.length > 0) words += countWords(strip(v));
    }
  }

  if (words === 0) return 0;
  return Math.max(1, Math.round(words / 200));
}
